'use client'

import * as React from 'react'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { downloadProjectStatistics } from '@/utils/download/downloadProjectStatistics'
import { formatCredits } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Clock,
  Coins,
  Bug,
  AlertTriangle,
  CheckCircle,
  XCircle,
  MessageSquare,
  FileText,
  Image,
  Download,
} from 'lucide-react'

interface ProjectStatisticsModalProps {
  projectId?: string
  userId?: string
}

interface StatisticsData {
  project: any
  initialPrompt: {
    content: string
    contentForAi?: string
    timestamp: string | number
    hasAssets: boolean
    assetCount: number
  } | null
  firstCoding: {
    messageId: string
    timestamp: number
    coins: number
    tokens: {
      prompt: number
      completion: number
      total: number
    }
    apiCost: number
    userFee: number
    model: string
    vendor: string
    deductFrom: number
    deductTo: number
    duration: number
  } | null
  subsequentCodings: Array<{
    sequence: number
    messageId: string
    timestamp: number
    coins: number
    tokens: {
      prompt: number
      completion: number
      total: number
    }
    apiCost: number
    userFee: number
    model: string
    vendor: string
    deductFrom: number
    deductTo: number
    timeSinceFirst: number
  }>
  totalStatistics: {
    totalCoins: number
    totalTokens: number
    totalApiCost: number
    totalUserFee: number
    totalDuration: number
    totalCodingCount: number
    bugCount: number
    errorTypes: {
      'Failed to load': number
      'Failed to response': number
      'Runtime error': number
      'Build error': number
      'Authentication error': number
      Other: number
    }
    modelTokenUsage: Record<
      string,
      {
        totalTokens: number
        promptTokens: number
        completionTokens: number
        usageCount: number
        totalCost: number
      }
    >
    errorLogs: Array<{
      timestamp: string
      errorMessage: string
      errorStack?: string
      endpoint: string
      metadata: any
    }>
  }
}

export default function ProjectStatisticsModal({
  projectId,
  userId,
}: ProjectStatisticsModalProps) {
  const [data, setData] = React.useState<StatisticsData | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [downloading, setDownloading] = React.useState(false)

  React.useEffect(() => {
    if (projectId || userId) {
      fetchStatistics()
    }
  }, [projectId, userId])

  const fetchStatistics = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      if (projectId) params.append('projectId', projectId)
      if (userId) params.append('userId', userId)

      const response = await fetch(
        `/api/dashboard/project-statistics?${params}`,
      )
      if (!response.ok) throw new Error('Failed to fetch statistics')

      const statisticsData = await response.json()
      setData(statisticsData)
    } catch (error) {
      console.error('Error fetching statistics:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDuration = (milliseconds: number) => {
    const seconds = Math.floor(milliseconds / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`
    }
    return `${seconds}s`
  }

  const formatTimestamp = (timestamp: number) => {
    return format(new Date(timestamp), 'yyyy-MM-dd HH:mm:ss')
  }

  const handleDownload = async () => {
    try {
      setDownloading(true)
      await downloadProjectStatistics(projectId, userId)
      toast.success('Project statistics downloaded as Excel')
    } catch (error) {
      console.error('Download failed:', error)
      toast.error('Failed to download project statistics')
    } finally {
      setDownloading(false)
    }
  }

  if (!data) {
    return (
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Project Statistics</DialogTitle>
          <DialogDescription>
            {loading ? 'Loading statistics...' : 'No data available'}
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    )
  }

  return (
    <DialogContent className="max-w-7xl max-h-[90vh]">
      <DialogHeader>
        <div className="flex items-center justify-between">
          <div>
            <DialogTitle>Project Statistics</DialogTitle>
            <DialogDescription>
              Detailed analysis of AI coding usage, costs, and error tracking
            </DialogDescription>
          </div>
          <div className="flex gap-2 mr-8">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              disabled={downloading}
            >
              <Download className="h-4 w-4 mr-2" />
              {downloading ? 'Downloading...' : 'Download Excel'}
            </Button>
          </div>
        </div>
      </DialogHeader>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="initial-prompt">Initial Prompt</TabsTrigger>
          <TabsTrigger value="subsequent">All Coding Sessions</TabsTrigger>
          <TabsTrigger value="model-usage">Model Usage</TabsTrigger>
          <TabsTrigger value="errors">Error Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Coins
                </CardTitle>
                <Coins className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCredits(data.totalStatistics.totalCoins)}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Tokens
                </CardTitle>
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {data.totalStatistics.totalTokens.toLocaleString()}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Duration
                </CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatDuration(data.totalStatistics.totalDuration)}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Bug Count</CardTitle>
                <Bug className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {data.totalStatistics.bugCount}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 项目基本信息 */}
          {data.project && (
            <Card>
              <CardHeader>
                <CardTitle>Project Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Project Name
                    </p>
                    <p className="text-sm">{data.project.project_name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Status
                    </p>
                    <Badge
                      variant={
                        data.project.status === 'live' ? 'default' : 'secondary'
                      }
                    >
                      {data.project.status}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Created
                    </p>
                    <p className="text-sm">
                      {formatTimestamp(data.project.create_time)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Coding Count
                    </p>
                    <p className="text-sm">
                      {data.totalStatistics.totalCodingCount}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* 错误类型统计 */}
          <Card>
            <CardHeader>
              <CardTitle>Error Types</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {Object.entries(data.totalStatistics.errorTypes).map(
                  ([type, count]) => (
                    <div key={type} className="flex items-center space-x-2">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{type}</p>
                        <p className="text-2xl font-bold">{count}</p>
                      </div>
                      {count > 0 ? (
                        <XCircle className="h-4 w-4 text-red-500" />
                      ) : (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      )}
                    </div>
                  ),
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 初始Prompt标签页 */}
        <TabsContent value="initial-prompt" className="space-y-4">
          {data.initialPrompt ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <MessageSquare className="h-5 w-5" />
                  <span>Initial User Prompt</span>
                  {data.initialPrompt.hasAssets && (
                    <Badge
                      variant="secondary"
                      className="flex items-center space-x-1"
                    >
                      <Image className="h-3 w-3" />
                      <span>{data.initialPrompt.assetCount} assets</span>
                    </Badge>
                  )}
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Timestamp:{' '}
                  {formatTimestamp(
                    typeof data.initialPrompt.timestamp === 'string'
                      ? new Date(data.initialPrompt.timestamp).getTime()
                      : data.initialPrompt.timestamp,
                  )}
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium mb-2">User Content:</h4>
                    <div className="bg-muted p-4 rounded-lg">
                      <ScrollArea className="h-48 w-full">
                        <p className="text-sm whitespace-pre-wrap pr-4">
                          {data.initialPrompt.content}
                        </p>
                      </ScrollArea>
                    </div>
                  </div>
                  {data.initialPrompt.contentForAi &&
                    data.initialPrompt.contentForAi !==
                      data.initialPrompt.content && (
                      <div>
                        <h4 className="text-sm font-medium mb-2">
                          AI Content (Processed):
                        </h4>
                        <div className="bg-muted p-4 rounded-lg">
                          <ScrollArea className="h-48 w-full">
                            <p className="text-sm whitespace-pre-wrap pr-4">
                              {data.initialPrompt.contentForAi}
                            </p>
                          </ScrollArea>
                        </div>
                      </div>
                    )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex items-center justify-center h-32">
                <p className="text-muted-foreground">No initial prompt found</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="subsequent" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>All AI Coding Sessions</CardTitle>
              <p className="text-sm text-muted-foreground">
                Total:{' '}
                {data.firstCoding
                  ? data.subsequentCodings.length + 1
                  : data.subsequentCodings.length}{' '}
                sessions
              </p>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sequence</TableHead>
                      <TableHead>Session Type</TableHead>
                      <TableHead>Time Since First</TableHead>
                      <TableHead>Coins</TableHead>
                      <TableHead>Tokens</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* First coding session */}
                    {data.firstCoding && (
                      <TableRow
                        key={`first-${data.firstCoding.messageId}-${data.firstCoding.timestamp}`}
                        className="bg-gold/5"
                      >
                        <TableCell>1</TableCell>
                        <TableCell>
                          <Badge variant="default" className="text-xs">
                            First
                          </Badge>
                        </TableCell>
                        <TableCell>0ms</TableCell>
                        <TableCell>
                          {formatCredits(data.firstCoding.coins)}
                        </TableCell>
                        <TableCell>
                          {data.firstCoding.tokens.total.toLocaleString()}
                        </TableCell>
                        <TableCell>{data.firstCoding.model}</TableCell>
                        <TableCell>
                          ${data.firstCoding.apiCost.toFixed(4)}
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Subsequent coding sessions */}
                    {data.subsequentCodings.map((coding) => (
                      <TableRow
                        key={`subsequent-${coding.messageId}-${coding.timestamp}`}
                      >
                        <TableCell>{coding.sequence}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            Subsequent
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {formatDuration(coding.timeSinceFirst)}
                        </TableCell>
                        <TableCell>{formatCredits(coding.coins)}</TableCell>
                        <TableCell>
                          {coding.tokens.total.toLocaleString()}
                        </TableCell>
                        <TableCell>{coding.model}</TableCell>
                        <TableCell>${coding.apiCost.toFixed(4)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 模型使用统计标签页 */}
        <TabsContent value="model-usage" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Model Token Usage Statistics</CardTitle>
              <p className="text-sm text-muted-foreground">
                Detailed breakdown by AI model
              </p>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Model</TableHead>
                      <TableHead>Usage Count</TableHead>
                      <TableHead>Total Tokens</TableHead>
                      <TableHead>Prompt Tokens</TableHead>
                      <TableHead>Completion Tokens</TableHead>
                      <TableHead>Total Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(data.totalStatistics.modelTokenUsage).map(
                      ([model, usage]) => (
                        <TableRow key={`model-${model}`}>
                          <TableCell className="font-medium">{model}</TableCell>
                          <TableCell>{usage.usageCount}</TableCell>
                          <TableCell>
                            {usage.totalTokens.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {usage.promptTokens.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {usage.completionTokens.toLocaleString()}
                          </TableCell>
                          <TableCell>${usage.totalCost.toFixed(4)}</TableCell>
                        </TableRow>
                      ),
                    )}

                    {/* 合计行 */}
                    <TableRow className="bg-muted/50 font-semibold">
                      <TableCell className="font-semibold">Total</TableCell>
                      <TableCell className="font-semibold">
                        {Object.values(
                          data.totalStatistics.modelTokenUsage,
                        ).reduce((sum, usage) => sum + usage.usageCount, 0)}
                      </TableCell>
                      <TableCell className="font-semibold">
                        {Object.values(data.totalStatistics.modelTokenUsage)
                          .reduce((sum, usage) => sum + usage.totalTokens, 0)
                          .toLocaleString()}
                      </TableCell>
                      <TableCell className="font-semibold">
                        {Object.values(data.totalStatistics.modelTokenUsage)
                          .reduce((sum, usage) => sum + usage.promptTokens, 0)
                          .toLocaleString()}
                      </TableCell>
                      <TableCell className="font-semibold">
                        {Object.values(data.totalStatistics.modelTokenUsage)
                          .reduce(
                            (sum, usage) => sum + usage.completionTokens,
                            0,
                          )
                          .toLocaleString()}
                      </TableCell>
                      <TableCell className="font-semibold">
                        $
                        {Object.values(data.totalStatistics.modelTokenUsage)
                          .reduce((sum, usage) => sum + usage.totalCost, 0)
                          .toFixed(4)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="errors" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Error Logs</CardTitle>
              <p className="text-sm text-muted-foreground">
                Recent error logs from AI coding sessions
              </p>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                {data.totalStatistics.errorLogs.length > 0 ? (
                  <div className="space-y-4">
                    {data.totalStatistics.errorLogs.map((log) => (
                      <div
                        key={`error-log-${log.timestamp}-${log.endpoint}-${log.errorMessage?.substring(0, 20)}`}
                        className="border rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-red-600">
                            {log.errorMessage}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {format(
                              new Date(log.timestamp),
                              'yyyy-MM-dd HH:mm:ss',
                            )}
                          </p>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">
                          Endpoint: {log.endpoint}
                        </p>
                        {log.errorStack && (
                          <details className="text-xs">
                            <summary className="cursor-pointer text-muted-foreground">
                              Show Stack Trace
                            </summary>
                            <ScrollArea className="mt-2 h-32 w-full">
                              <pre className="bg-muted p-2 rounded pr-4">
                                {log.errorStack}
                              </pre>
                            </ScrollArea>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-32">
                    <p className="text-muted-foreground">No error logs found</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </DialogContent>
  )
}
